import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  useDisclosure,
  useToast,
  FormControl,
  FormLabel,
  Input,
  InputGroup,
  InputRightElement,
  Text,
} from "@chakra-ui/react";
import axios from "axios";
import { useMemo, useState } from "react";
import { ChatState } from "../../Context/ChatProvider";

const strengthOf = (pw) => {
  if (!pw) return { pct: 0, label: "", color: "transparent" };
  let score = 0;
  if (pw.length >= 6) score += 1;
  if (pw.length >= 10) score += 1;
  if (/[A-Z]/.test(pw) && /[a-z]/.test(pw)) score += 1;
  if (/\d/.test(pw)) score += 1;
  if (/[^A-Za-z0-9]/.test(pw)) score += 1;
  if (score <= 2) return { pct: 33, label: "Weak — go harder", color: "#f87171" };
  if (score <= 3) return { pct: 62, label: "Solid — almost elite", color: "#fbbf24" };
  return { pct: 100, label: "Beast mode 🔥", color: "#34d399" };
};

// Logged-in password change: current + new (PUT /api/user/change-password).
// Separate from the forgot-password OTP flow (unauthenticated).
const ChangePasswordModal = ({ children }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();
  const { user } = ChatState();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);

  const strength = useMemo(() => strengthOf(newPassword), [newPassword]);

  const reset = () => {
    setCurrentPassword("");
    setNewPassword("");
    setConfirm("");
    setLoading(false);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const submit = async (e) => {
    if (e) e.preventDefault();
    if (!currentPassword || !newPassword || !confirm) {
      toast({
        title: "Please fill all the fields",
        status: "warning",
        duration: 4000,
        isClosable: true,
        position: "bottom",
      });
      return;
    }
    if (newPassword !== confirm) {
      toast({
        title: "Passwords Do Not Match",
        status: "warning",
        duration: 4000,
        isClosable: true,
        position: "bottom",
      });
      return;
    }
    setLoading(true);
    try {
      const config = {
        headers: { Authorization: `Bearer ${user?.token}` },
      };
      const { data } = await axios.put(
        "/api/user/change-password",
        { currentPassword, newPassword },
        config
      );
      toast({
        title: "Password changed ✓",
        description: data.message || "Use your new password next time you log in.",
        status: "success",
        duration: 5000,
        isClosable: true,
        position: "bottom",
      });
      handleClose();
    } catch (error) {
      toast({
        title: "Couldn't change password",
        description: error.response?.data?.message || error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "bottom",
      });
      setLoading(false);
    }
  };

  return (
    <>
      <span onClick={onOpen} style={{ display: "contents" }}>
        {children}
      </span>
      <Modal size="md" onClose={handleClose} isOpen={isOpen} isCentered>
        <ModalOverlay bg="rgba(2,6,23,0.7)" backdropFilter="blur(6px)" />
        <ModalContent
          bg="var(--ch-modal)"
          color="var(--ch-text)"
          border="1px solid var(--ch-panel-border)"
          borderRadius="20px"
        >
          <ModalHeader fontFamily="Sora" fontWeight="800">
            Change Password
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody as="form" onSubmit={submit}>
            <FormControl isRequired mb={3}>
              <FormLabel fontSize="sm">Current password</FormLabel>
              <Input
                type={show ? "text" : "password"}
                placeholder="••••••••••"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                className="chat-input"
              />
            </FormControl>
            <FormControl isRequired mb={3}>
              <FormLabel fontSize="sm">New password</FormLabel>
              <InputGroup>
                <Input
                  type={show ? "text" : "password"}
                  placeholder="Make it unbreakable"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="chat-input"
                  pr="4.8rem"
                />
                <InputRightElement width="4.5rem" h="100%">
                  <Button h="1.75rem" size="sm" onClick={() => setShow((v) => !v)}>
                    {show ? "Hide" : "Show"}
                  </Button>
                </InputRightElement>
              </InputGroup>
              {newPassword && (
                <>
                  <div className="strength-track">
                    <div
                      className="strength-fill"
                      style={{
                        width: `${strength.pct}%`,
                        background: strength.color,
                      }}
                    />
                  </div>
                  <div
                    className="strength-label"
                    style={{ color: strength.color }}
                  >
                    {strength.label}
                  </div>
                </>
              )}
            </FormControl>
            <FormControl isRequired>
              <FormLabel fontSize="sm">Confirm new password</FormLabel>
              <Input
                type={show ? "text" : "password"}
                placeholder="One more time"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="chat-input"
              />
              {!!confirm && !!newPassword && (
                <Text
                  fontSize="xs"
                  fontWeight="700"
                  mt={1}
                  color={newPassword === confirm ? "green.300" : "red.300"}
                >
                  {newPassword === confirm
                    ? "Passwords match ✓"
                    : "Passwords don't match yet"}
                </Text>
              )}
            </FormControl>
          </ModalBody>
          <ModalFooter gap={2}>
            <Button variant="ghost" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              colorScheme="cyan"
              onClick={submit}
              isLoading={loading}
              loadingText="Saving…"
            >
              Change Password
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default ChangePasswordModal;
